import { Button, ColumnTable, Icon } from '@octano/global-ui';
import { useTranslation } from 'react-i18next';
import { Input } from 'reactstrap';

import Tooltip from '../../../../../components/tooltip/Tooltip';
import { moneyFormatter } from '../../../../../utils/currency';
import { formatDate } from '../../../../../utils/dates';
import { Debt, DebtTableRow } from '../types';

const dictPrefix = 'financialPortal.currentAccount.tabs.currentAccount.table';

interface ActionsColumns {
  onClickDetail: (row: Debt) => void;
  onClickApplyRefund: (refundId: string) => void;
  selectedDebts: Debt[];
  onClickNotifySelectedDebts: (row: Debt) => void;
  allowExternalDebtPaymentLink: boolean;
  allowMultiDebtPaymentLink: boolean;
}

const NA = 'N/A';

const moraClassName = (baseClassName: string) => (options: {
  row: DebtTableRow;
}) => {
  if (options.row.__kind !== 'debt') {
    return baseClassName;
  }
  const isLate = (options.row.daysLate ?? 0) > 0;
  return `${baseClassName}${isLate ? ' text-danger' : ''}`;
};

export default function useColumns(
  parms: ActionsColumns,
): ColumnTable<DebtTableRow>[] {
  const {
    onClickDetail,
    onClickApplyRefund,
    selectedDebts,
    onClickNotifySelectedDebts,
    allowExternalDebtPaymentLink,
    allowMultiDebtPaymentLink,
  } = parms;
  const { t } = useTranslation();

  const centeredMoraClassName = moraClassName('text-center');

  return [
    {
      columnName: 'studyPlanName',
      headerText: t(`${dictPrefix}.studyPlanName`),
      tdClassName: centeredMoraClassName,
      thClassName: 'text-center',
      cellFormat: ({ row }) =>
        row.__kind === 'debt' ? row.studyPlanName ?? '-' : NA,
    },
    {
      columnName: 'concept',
      headerText: t(`${dictPrefix}.charge`),
      tdClassName: centeredMoraClassName,
      thClassName: 'text-center',
      cellFormat: ({ row }) => {
        if (row.__kind === 'debt') {
          return row.concept ?? '-';
        }
        const dateLabel = formatDate(row.refund.createdAt, 'DD/MM/YYYY');
        return (
          <span className="text-primary fw-600">
            {t(`${dictPrefix}.refundConcept`, {
              concept: row.refund.concept,
            })}{' '}
            - {dateLabel}
          </span>
        );
      },
    },
    {
      columnName: 'idToDisplay',
      headerText: t(`${dictPrefix}.idToDisplay`),
      tdClassName: centeredMoraClassName,
      thClassName: 'text-center',
      cellFormat: ({ row }) =>
        row.__kind === 'debt' ? row.idToDisplay ?? '-' : NA,
    },
    {
      columnName: 'year',
      headerText: t(`${dictPrefix}.year`),
      tdClassName: centeredMoraClassName,
      thClassName: 'text-center',
      cellFormat: ({ row }) => (row.__kind === 'debt' ? row.year : NA),
    },
    {
      columnName: 'periodName',
      headerText: t(`${dictPrefix}.period`),
      tdClassName: centeredMoraClassName,
      thClassName: 'text-center',
      cellFormat: ({ row }) =>
        row.__kind === 'debt' ? row.periodName ?? '-' : NA,
    },
    {
      columnName: 'numberOfInstallment',
      headerText: t(`${dictPrefix}.installment`),
      tdClassName: centeredMoraClassName,
      thClassName: 'text-center',
      cellFormat: ({ row }) =>
        row.__kind === 'debt' ? row.numberOfInstallment : NA,
    },
    {
      columnName: 'initialAmount',
      headerText: t(`${dictPrefix}.initialAmount`),
      tdClassName: centeredMoraClassName,
      thClassName: 'text-center',
      cellFormat: ({ row }) => {
        if (row.__kind === 'debt') {
          return moneyFormatter().format(
            Math.round(Number(row.initialAmount ?? 0)),
          );
        }
        return moneyFormatter().format(
          Math.ceil(Number(row.refund.amountGross ?? 0)),
        );
      },
    },
    {
      columnName: 'paidAmount',
      headerText: t(`${dictPrefix}.paidAmount`),
      tdClassName: centeredMoraClassName,
      thClassName: 'text-center',
      cellFormat: ({ row }) => {
        if (row.__kind === 'debt') {
          return moneyFormatter().format(
            Math.round(Number(row.paidAmount ?? 0)),
          );
        }
        const consumed =
          Number(row.refund.amountGross ?? 0) - Number(row.refund.amount ?? 0);
        return moneyFormatter().format(Math.ceil(consumed));
      },
    },
    {
      columnName: 'interestRate',
      headerText: t(`${dictPrefix}.interestRate`),
      tdClassName: centeredMoraClassName,
      thClassName: 'text-center',
      cellFormat: ({ row }) =>
        row.__kind === 'debt'
          ? moneyFormatter().format(Math.round(Number(row.interestRate ?? 0)))
          : NA,
    },
    {
      columnName: 'collectionFees',
      headerText: t(`${dictPrefix}.collectionFees`),
      tdClassName: centeredMoraClassName,
      thClassName: 'text-center',
      cellFormat: ({ row }) =>
        row.__kind === 'debt'
          ? moneyFormatter().format(Math.round(Number(row.collectionFees ?? 0)))
          : NA,
    },
    {
      columnName: 'outstandingDebt',
      headerText: t(`${dictPrefix}.debt`),
      tdClassName: centeredMoraClassName,
      thClassName: 'text-center',
      cellFormat: ({ row }) => {
        if (row.__kind === 'debt') {
          return moneyFormatter().format(
            Math.round(Number(row.outstandingDebt ?? 0)),
          );
        }
        const balance = Number(row.refund.amount ?? 0);
        return (
          <span style={{ color: '#B07B00', fontWeight: 700 }}>
            + {moneyFormatter().format(Math.ceil(balance))}
          </span>
        );
      },
    },
    {
      columnName: 'expirationDate',
      headerText: t(`${dictPrefix}.expirationDate`),
      tdClassName: centeredMoraClassName,
      thClassName: 'text-center',
      cellFormat: ({ row }) => {
        if (row.__kind !== 'debt') {
          return NA;
        }
        return row.expirationDate
          ? formatDate(row.expirationDate, 'DD/MM/YYYY')
          : '-';
      },
    },
    {
      columnName: 'action',
      headerText: t(`${dictPrefix}.action`),
      tdClassName: 'text-center',
      thClassName: 'text-center',
      width: 180,
      cellFormat: ({ row }) => {
        if (row.__kind === 'refund') {
          return (
            <div className="d-flex justify-content-center">
              <Button
                text={t(`${dictPrefix}.applyRefund`)}
                size="sm"
                outlined
                onClick={() => onClickApplyRefund(row.id)}
              />
            </div>
          );
        }
        return (
          <div className="d-flex justify-content-center">
            <button
              type="button"
              className="border-0 bg-transparent p-0"
              aria-label={t(`${dictPrefix}.viewDetail`)}
              onClick={() => onClickDetail(row)}
            >
              <Icon name="eye" />
            </button>
          </div>
        );
      },
    },
    {
      columnName: '',
      headerText: '',
      cellFormat: (options) => {
        if (options.row.__kind !== 'debt') {
          return null;
        }
        const debtRow = options.row;
        const isSelected = selectedDebts.some((d) => d.id === debtRow.id);
        // Esta columna sólo alimenta "Generar link de pago": el motivo por el
        // que una fila no se puede marcar se muestra en tooltip, y el backend
        // revalida todo igual al generar el link.
        const disabledReason = ((): string | null => {
          // Una fila ya marcada siempre se puede desmarcar.
          if (isSelected) {
            return null;
          }
          if (Number(debtRow.outstandingDebt ?? 0) <= 0) {
            return 'alreadyPaidTooltip';
          }
          if (!allowExternalDebtPaymentLink) {
            return null;
          }
          const payable = debtRow.payableExternalDebt;
          if (!payable) {
            return 'notPayableTooltip';
          }
          if (!selectedDebts.length) {
            return null;
          }
          if (!allowMultiDebtPaymentLink) {
            return 'singleOnlyTooltip';
          }
          // El mismatch de proveedor va ANTES que la capability de la fila:
          // ambos motivos pueden ser ciertos a la vez, y "sólo se pueden
          // combinar cuotas de la misma pasarela" es el accionable de los
          // dos — decirle a alguien "esta pasarela sólo permite una cuota a
          // la vez" cuando el problema real es que mezcló proveedores no le
          // dice qué hacer.
          const selectedProvider =
            selectedDebts[0].payableExternalDebt?.provider ?? null;
          if (selectedProvider !== null && payable.provider !== selectedProvider) {
            return 'otherProviderTooltip';
          }
          if (!payable.supportsMultiDebtPay) {
            return 'singleOnlyTooltip';
          }
          return null;
        })();
        const checkbox = (
          <div className="form-group">
            <label className="g-checkbox-input-container">
              <Input
                className="g-checkbox-input"
                type="checkbox"
                id=""
                name=""
                disabled={disabledReason !== null}
                checked={isSelected}
                onChange={() => onClickNotifySelectedDebts(debtRow)}
              />
              <span className="checkmark"></span>
            </label>
            <small className="g-input-error form-text text-danger"></small>
          </div>
        );
        if (disabledReason === null) {
          return checkbox;
        }
        return (
          <Tooltip
            id={`debt-not-selectable-${debtRow.id}`}
            text={t(`${dictPrefix}.${disabledReason}`)}
          >
            {checkbox}
          </Tooltip>
        );
      },
    },
  ];
}
